import { match, P } from "ts-pattern"
import * as F from "fp-ts/function"
import * as O from "fp-ts/Option"
import { waitFor } from "xstate/lib/waitFor"
import {
    ECommerceMachineStateValue,
    ECommerceSend,
    ECommerceService,
    ECommerceState,
} from "../machines/ecommerce-machine/e-commerce-machine"
import { Command } from "../../../_common/diff-url-query-params"
import { decodeGuests } from "../../../_common/guests-encoding"
import { decodeGateway } from "../../../_common/gateway-encoding"
import { decodeAirFare } from "../../../_common/airfare-encoding"
import { decodeVs } from "../../../_common/vs-encoding"
import { decodeSuite } from "../../../_common/suite-encoding"

export type MachineAction = () => Promise<unknown>

export const commandToActionV3 =
    (send: ECommerceSend, ecommerceActor: ECommerceService) =>
    (command: Command): MachineAction => {
        return match(command)
            .with({ key: P.union("guests", "airfare", "gateway"), type: P.union("add", "remove") }, nothingToDo)
            .with({ key: "guests", type: "update" }, ({ value }) => async () => {
                const guests = decodeGuests(value)
                send({ type: "CHANGE_GUESTS", guests })
                await waitForState(ecommerceActor, "flow.nothingSelected.fareSelectable")
            })
            .with({ key: "fare", type: P.union("update", "add") }, ({ value }) => async () => {
                send({ type: "SELECT_FARE", key: value })
                await waitForState(ecommerceActor, "flow.fareSelected.suiteCategorySelectable")
            })
            .with({ key: "fare", type: "remove" }, () => async () => {
                send({ type: "UNSELECT_FARE" })
            })
            .with({ key: "gateway", type: "update" }, ({ value }) => async () => {
                F.pipe(
                    decodeGateway(value),
                    O.map(({ key, gateway }) => send({ type: "CHANGE_GATEWAY", key, gatewayCode: gateway })),
                )
            })
            .with({ key: "airfare", type: "update" }, ({ value }) => async () => {
                F.pipe(
                    decodeAirFare(value),
                    O.map(({ key, airClass }) => send({ type: "CHANGE_AIR_CLASS", key, airClass })),
                )
            })
            .with({ key: "suite", type: P.union("update", "add") }, ({ value }) => async () => {
                return F.pipe(
                    decodeSuite(value),
                    O.map(({ suiteCategory, source }) =>
                        match(source)
                            .with("standard", async () => {
                                send({ type: "SELECT_SUITE_CATEGORY", suiteCategory })
                                await waitForState(ecommerceActor, "flow.suiteCategorySelected.suiteNumberSelectable")
                            })
                            .with("quote", () => {
                                // NOTE: we don't support selecting a quote suite category
                                return Promise.resolve()
                            })
                            .exhaustive(),
                    ),
                )
            })
            .with({ key: "suite", type: "remove" }, () => async () => {
                send({ type: "UNSELECT_SUITE_CATEGORY" })
            })
            .with({ key: "suite-number", type: P.union("update", "add") }, ({ value }) => async () => {
                await waitForState(ecommerceActor, "flow.suiteCategorySelected.suiteNumberSelectable")
                send({ type: "SELECT_SUITE_NUMBER", suiteNumber: value })
            })
            .with({ key: "suite-number", type: "remove" }, () => async () => {
                send({ type: "UNSELECT_SUITE_NUMBER" })
            })
            .with({ key: "vs", type: P.union("update", "add") }, ({ value }) => async () => {
                send({ type: "CHANGE_VS_MEMBER", maybeVsMember: decodeVs(value) })
            })
            .with({ key: "vs", type: "remove" }, () => async () => {
                send({ type: "CHANGE_VS_MEMBER", maybeVsMember: false })
            })
            .with({ key: "checkout", type: P.union("add", "remove", "update") }, nothingToDo)
            .with({ key: "modal", type: P.union("add", "remove", "update") }, nothingToDo)
            .exhaustive()
    }

const waitForState = async (actor: ECommerceService, stateValue: ECommerceMachineStateValue) => {
    try {
        await waitFor(actor, (state: ECommerceState) => state.matches(stateValue))
    } catch (e) {
        // TODO: replace with sendNF
        console.error(`Error in waitFor '${stateValue}'`, e)
    }
}

const nothingToDo = () => async () => {}
